import React, { useState } from 'react';
import SEO from '../components/layout/SEO';
import { Building2, Users, Mail, Phone, Globe, MapPin, User, Shield, CheckCircle, AlertCircle } from 'lucide-react';

interface TrialFormData {
  // Institute Information
  instituteName: string;
  instituteType: string;
  website: string;
  address: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  
  // Contact Information
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  contactPosition: string;
  
  // Technical Information
  expectedUsers: string;
  primaryUseCase: string;
  currentLMS: string;
  integrationNeeds: string;
  
  // Additional Information
  description: string;
  howDidYouHear: string;
  
  // Terms and Conditions
  agreeToTerms: boolean;
  agreeToMarketing: boolean;
}

const StartTrial: React.FC = () => {
  const [formData, setFormData] = useState<TrialFormData>({
    instituteName: '',
    instituteType: '',
    website: '',
    address: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    contactPosition: '',
    expectedUsers: '',
    primaryUseCase: '',
    currentLMS: '',
    integrationNeeds: '',
    description: '',
    howDidYouHear: '',
    agreeToTerms: false,
    agreeToMarketing: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const instituteTypes = [
    'K-12 School',
    'K-12 District',
    'University',
    'College',
    'Community College',
    'Corporate Training',
    'Online Education Provider',
    'Tutoring Center',
    'Educational Consultant',
    'Other'
  ];

  const useCases = [
    'Student Learning Management',
    'Teacher Professional Development',
    'Corporate Training',
    'Online Course Delivery',
    'Assessment and Testing',
    'Parent Communication',
    'Administrative Management',
    'Research and Analytics',
    'Other'
  ];

  const currentLMSOptions = [
    'None - First time using an LMS',
    'Canvas',
    'Blackboard',
    'Moodle',
    'Google Classroom',
    'Schoology',
    'D2L Brightspace',
    'Other'
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await fetch('https://api.tadriso.ciphermind.tech/api/TrialRequests/trials', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          // Institute Information
          institute: {
            name: formData.instituteName,
            type: formData.instituteType,
            website: formData.website,
            address: {
              street: formData.address,
              city: formData.city,
              state: formData.state,
              country: formData.country,
              postalCode: formData.postalCode
            }
          },
          // Contact Information
          contact: {
            name: formData.contactName,
            email: formData.contactEmail,
            phone: formData.contactPhone,
            position: formData.contactPosition
          },
          // Technical Information
          technical: {
            expectedUsers: parseInt(formData.expectedUsers) || 0,
            primaryUseCase: formData.primaryUseCase,
            currentLMS: formData.currentLMS,
            integrationNeeds: formData.integrationNeeds
          },
          // Additional Information
          description: formData.description,
          howDidYouHear: formData.howDidYouHear,
          marketingConsent: formData.agreeToMarketing,
          termsAccepted: formData.agreeToTerms,
          // Metadata
          source: 'website',
          timestamp: new Date().toISOString()
        })
      });

      if (response.ok) {
        const result = await response.json();
        setSubmitSuccess(true);
        
        // Redirect to LMS after 3 seconds
        setTimeout(() => {
          window.location.href = 'https://tadriso.ciphermind.tech';
        }, 3000);
      } else {
        const errorData = await response.json();
        setSubmitError(errorData.message || 'Failed to submit trial request. Please try again.');
      }
    } catch (error) {
      setSubmitError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <CheckCircle size={64} className="text-green-500 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Trial Request Submitted!
            </h2>
            <p className="text-gray-600 mb-6">
              Thank you for your interest in tadriso LMS. We've received your trial request and will contact you within 24 hours to set up your account.
            </p>
            <div className="bg-blue-50 rounded-lg p-4 mb-6">
              <p className="text-blue-800 text-sm">
                You will be redirected to tadriso LMS in a few seconds...
              </p>
            </div>
            <a
              href="https://tadriso.ciphermind.tech"
              className="inline-flex items-center px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-colors duration-200"
            >
              Go to tadriso LMS Now
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Start Free Trial - tadriso LMS"
        description="Start your free trial of tadriso LMS. Complete the form to get started with our comprehensive learning management system."
        keywords="free trial, LMS trial, learning management system, start trial, tadriso LMS"
      />
      
      {/* Hero Section */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Start Your
              <span className="text-primary-600 block">Free Trial</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get started with tadriso LMS today. Complete the form below and we'll set up your free trial account within 24 hours.
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Institute Information */}
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="flex items-center space-x-3 mb-6">
                <Building2 size={24} className="text-primary-600" />
                <h2 className="text-2xl font-bold text-gray-900">Institute Information</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="instituteName" className="block text-sm font-medium text-gray-700 mb-2">
                    Institute Name *
                  </label>
                  <input
                    type="text"
                    id="instituteName"
                    name="instituteName"
                    value={formData.instituteName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter your institute name"
                  />
                </div>

                <div>
                  <label htmlFor="instituteType" className="block text-sm font-medium text-gray-700 mb-2">
                    Institute Type *
                  </label>
                  <select
                    id="instituteType"
                    name="instituteType"
                    value={formData.instituteType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Select institute type</option>
                    {instituteTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
                    Website
                  </label>
                  <input
                    type="url"
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="https://your-institute.com"
                  />
                </div>

                <div>
                  <label htmlFor="expectedUsers" className="block text-sm font-medium text-gray-700 mb-2">
                    Expected Number of Users *
                  </label>
                  <input
                    type="number"
                    id="expectedUsers"
                    name="expectedUsers"
                    value={formData.expectedUsers}
                    onChange={handleChange}
                    required
                    min="1"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="e.g., 500"
                  />
                </div>
              </div>

              <div className="mt-6">
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                  Address *
                </label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Street address"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                    City *
                  </label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="City"
                  />
                </div>

                <div>
                  <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">
                    State/Province *
                  </label>
                  <input
                    type="text"
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="State"
                  />
                </div>

                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                    Country *
                  </label>
                  <input
                    type="text"
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Country"
                  />
                </div>

                <div>
                  <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-2">
                    Postal Code *
                  </label>
                  <input
                    type="text"
                    id="postalCode"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Postal code"
                  />
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="flex items-center space-x-3 mb-6">
                <User size={24} className="text-primary-600" />
                <h2 className="text-2xl font-bold text-gray-900">Contact Information</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="contactName" className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Name *
                  </label>
                  <input
                    type="text"
                    id="contactName"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Full name"
                  />
                </div>

                <div>
                  <label htmlFor="contactPosition" className="block text-sm font-medium text-gray-700 mb-2">
                    Position *
                  </label>
                  <input
                    type="text"
                    id="contactPosition"
                    name="contactPosition"
                    value={formData.contactPosition}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="e.g., IT Director, Principal"
                  />
                </div>

                <div>
                  <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="contactEmail"
                    name="contactEmail"
                    value={formData.contactEmail}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="your.email@institute.com"
                  />
                </div>

                <div>
                  <label htmlFor="contactPhone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="contactPhone"
                    name="contactPhone"
                    value={formData.contactPhone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="+92 (308) 7700430"
                  />
                </div>
              </div>
            </div>

            {/* Technical Information */}
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="flex items-center space-x-3 mb-6">
                <Shield size={24} className="text-primary-600" />
                <h2 className="text-2xl font-bold text-gray-900">Technical Information</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="primaryUseCase" className="block text-sm font-medium text-gray-700 mb-2">
                    Primary Use Case *
                  </label>
                  <select
                    id="primaryUseCase"
                    name="primaryUseCase"
                    value={formData.primaryUseCase}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Select primary use case</option>
                    {useCases.map((useCase) => (
                      <option key={useCase} value={useCase}>{useCase}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="currentLMS" className="block text-sm font-medium text-gray-700 mb-2">
                    Current LMS (if any)
                  </label>
                  <select
                    id="currentLMS"
                    name="currentLMS"
                    value={formData.currentLMS}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Select current LMS</option>
                    {currentLMSOptions.map((lms) => (
                      <option key={lms} value={lms}>{lms}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-6">
                <label htmlFor="integrationNeeds" className="block text-sm font-medium text-gray-700 mb-2">
                  Integration Needs
                </label>
                <textarea
                  id="integrationNeeds"
                  name="integrationNeeds"
                  value={formData.integrationNeeds}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Describe any specific integrations you need (e.g., SIS, SSO, etc.)"
                />
              </div>
            </div>

            {/* Additional Information */}
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Additional Information</h2>
              
              <div className="space-y-6">
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                    Project Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Tell us about your project and how you plan to use tadriso LMS..."
                  />
                </div>

                <div>
                  <label htmlFor="howDidYouHear" className="block text-sm font-medium text-gray-700 mb-2">
                    How did you hear about us?
                  </label>
                  <select
                    id="howDidYouHear"
                    name="howDidYouHear"
                    value={formData.howDidYouHear}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Select an option</option>
                    <option value="Search Engine">Search Engine</option>
                    <option value="Social Media">Social Media</option>
                    <option value="Referral">Referral</option>
                    <option value="Advertisement">Advertisement</option>
                    <option value="Conference/Event">Conference/Event</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Terms and Conditions */}
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Terms and Conditions</h2>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="agreeToTerms"
                    name="agreeToTerms"
                    checked={formData.agreeToTerms}
                    onChange={handleChange}
                    required
                    className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="agreeToTerms" className="text-sm text-gray-700">
                    I agree to the <a href="/terms" className="text-primary-600 hover:text-primary-700">Terms of Service</a> and <a href="/privacy" className="text-primary-600 hover:text-primary-700">Privacy Policy</a> *
                  </label>
                </div>

                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="agreeToMarketing"
                    name="agreeToMarketing"
                    checked={formData.agreeToMarketing}
                    onChange={handleChange}
                    className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="agreeToMarketing" className="text-sm text-gray-700">
                    I agree to receive marketing communications from CipherMind Technologies (optional)
                  </label>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {submitError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <AlertCircle size={20} className="text-red-500" />
                  <p className="text-red-700">{submitError}</p>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="text-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-8 py-4 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white font-semibold rounded-lg transition-colors duration-200 flex items-center justify-center mx-auto"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                    Submitting...
                  </>
                ) : (
                  'Start Free Trial'
                )}
              </button>
              <p className="text-sm text-gray-500 mt-4">
                We'll contact you within 24 hours to set up your trial account.
              </p>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default StartTrial; 